/* GCS Kategorie und Suche (Baustein 6)
   <gcs-collection data-section-id data-url> umschließt gcs-main-collection und gcs-main-search.
   Filtern, Sortieren und „Mehr laden“ ohne Neuladen über die Section Rendering API (?section_id=…),
   URL per history.replaceState. Ohne JS bleibt alles ein normales GET-Formular mit echten Links.
   Mobil: Filter im Bottom-Sheet (<gcs-drawer>), Änderungen zeigen die Trefferzahl im Button, „Anwenden“ übernimmt.
   Desktop (ab 990 px): dasselbe Formular als Sidebar, Änderungen wirken sofort.
   Teile, die nach jeder Antwort ersetzt werden: [data-gcs-part="count|toolbar|active|reset|facets|results|pagination"].
   Nach jedem übernommenen Filterstand: Event gcs:collection:updated (bubbles, detail.url). <gcs-style-tabs>
   (Section gcs-collection-style-tabs, außerhalb von <gcs-collection>) lädt sich darauf per Section Rendering nach. */
(() => {
  if (customElements.get('gcs-collection')) return;
  const GCS = (window.GCS = window.GCS || {});
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const PARTS = ['count', 'toolbar', 'active', 'reset', 'results', 'pagination'];
  const ERROR = 'Das hat nicht geklappt. Bitte versuch es noch einmal.';
  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  class GcsCollection extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.seq = 0;
      this.classList.add('is-enhanced');

      this.applyDebounced = debounce(() => this.apply(this.urlFromForm()), 280);
      this.previewDebounced = debounce(() => this.preview(), 450);

      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('gcs:drawer:open', (event) => this.onDrawerOpen(event));
      this.addEventListener('gcs:drawer:close', (event) => this.onDrawerClose(event));

      this.syncMode = this.syncMode.bind(this);
      if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', this.syncMode);
      else if (DESKTOP.addListener) DESKTOP.addListener(this.syncMode);
      this.syncMode();
      this.snapshotFacets();
    }

    disconnectedCallback() {
      if (DESKTOP.removeEventListener) DESKTOP.removeEventListener('change', this.syncMode);
    }

    /* Elemente ------------------------------------------------------------ */
    get drawer() {
      return this.querySelector('[data-gcs-filters]');
    }

    get form() {
      return this.querySelector('[data-gcs-facet-form]') || document.getElementById('GcsFacetForm-' + this.sectionId);
    }

    get sortSelect() {
      return this.querySelector('[data-gcs-sort]');
    }

    isSheet() {
      const drawer = this.drawer;
      return !DESKTOP.matches && !!drawer && drawer.hasAttribute('open');
    }

    /* Desktop: Sidebar ist kein Dialog; mobil: Sheet mit role=dialog */
    syncMode() {
      const drawer = this.drawer;
      if (!drawer) return;
      const panel = drawer.querySelector('[data-gcs-filter-panel]');
      if (DESKTOP.matches) {
        if (drawer.hasAttribute('open') && typeof drawer.close === 'function') drawer.close({ restoreFocus: false });
        if (panel) {
          panel.setAttribute('role', 'region');
          panel.removeAttribute('aria-modal');
        }
      } else if (panel) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
      }
    }

    /* URL aus dem Formular ------------------------------------------------ */
    urlFromForm() {
      const form = this.form;
      const select = this.sortSelect;
      if (!form) {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        if (select) url.searchParams.set('sort_by', select.value);
        return url.pathname + url.search;
      }
      const params = new URLSearchParams();
      new FormData(form).forEach((raw, key) => {
        if (typeof raw !== 'string') return;
        let value = raw.trim();
        if (!value) return;
        if (key.indexOf('filter.v.price') === 0) {
          value = value.replace(/\s/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.');
          if (!/^\d+(\.\d+)?$/.test(value)) return;
        }
        if (key === 'sort_by' && select && value === select.dataset.default) return;
        params.append(key, value);
      });
      const action = form.getAttribute('action') || this.dataset.url || window.location.pathname;
      const query = params.toString();
      return action + (query ? '?' + query : '');
    }

    sectionUrl(url) {
      const target = new URL(url, window.location.origin);
      target.searchParams.delete('section_id');
      target.searchParams.set('section_id', this.sectionId);
      return target.pathname + target.search;
    }

    async fetchDoc(url, signal) {
      const key = this.sectionUrl(url);
      let html = this.cache.get(key);
      if (!html) {
        const response = await fetch(key, { signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(String(response.status));
        html = await response.text();
        if (this.cache.size > 24) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(key, html);
      }
      return new DOMParser().parseFromString(html, 'text/html');
    }

    /* Ereignisse ---------------------------------------------------------- */
    onChange(event) {
      const target = event.target;
      if (target.matches('[data-gcs-sort]')) {
        this.apply(this.urlFromForm());
        return;
      }
      if (!target.closest('[data-gcs-facet-form]')) return;
      if (this.isSheet()) this.preview();
      else if (DESKTOP.matches) this.applyDebounced();
    }

    onInput(event) {
      const target = event.target;
      if (!target.closest('[data-gcs-price-range]')) return;
      if (this.isSheet()) this.previewDebounced();
    }

    onSubmit(event) {
      const form = event.target;
      if (!form.matches('[data-gcs-facet-form]') && form !== this.form) return;
      event.preventDefault();
      const url = this.urlFromForm();
      const drawer = this.drawer;
      const fromSheet = !!(drawer && drawer.hasAttribute('open'));
      if (fromSheet) {
        this.applied = true;
        drawer.close();
      }
      this.apply(url, { focusOpener: fromSheet });
    }

    onClick(event) {
      const reset = event.target.closest('[data-gcs-facet-reset]');
      if (reset && this.isSheet()) {
        event.preventDefault();
        this.clearForm();
        this.preview();
        return;
      }
      const link = event.target.closest('[data-gcs-facet-link], [data-gcs-facet-reset]');
      if (link && link.href) {
        event.preventDefault();
        const drawer = this.drawer;
        if (drawer && drawer.hasAttribute('open')) {
          this.applied = true;
          drawer.close({ restoreFocus: false });
        }
        this.apply(link.href, { focusResults: true });
        return;
      }
      const more = event.target.closest('[data-gcs-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
      }
    }

    onDrawerOpen(event) {
      if (!this.drawer || event.target !== this.drawer) return;
      this.applied = false;
      this.openUrl = this.urlFromForm();
      const count = this.querySelector('[data-gcs-part="count"]');
      this.setApplyLabel(count ? Number(count.dataset.gcsCount) : null);
    }

    onDrawerClose(event) {
      if (!this.drawer || event.target !== this.drawer) return;
      if (this.previewController) this.previewController.abort();
      /* Geschlossen ohne „Anwenden“: Auswahl im Sheet auf den angezeigten Stand zurücksetzen */
      if (!this.applied && this.urlFromForm() !== this.openUrl) this.restoreFacets();
      this.applied = false;
    }

    /* Filter übernehmen --------------------------------------------------- */
    async apply(url, { focusResults = false, focusOpener = false } = {}) {
      const seq = ++this.seq;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setLoading(true);
      try {
        const doc = await this.fetchDoc(url, this.controller.signal);
        if (seq !== this.seq) return;
        this.render(doc);
        const clean = new URL(url, window.location.origin);
        clean.searchParams.delete('section_id');
        window.history.replaceState(window.history.state, '', clean.pathname + clean.search);
        this.dispatchEvent(
          new CustomEvent('gcs:collection:updated', { bubbles: true, detail: { url: clean.pathname + clean.search } })
        );
        this.announce();
        this.scrollToResults();
        if (focusResults) this.focusResults();
        /* QA: „Anwenden“ im Sheet: der Filter-Button, zu dem das Sheet den Fokus zurückgibt, wird beim Rendern
           ersetzt. Ohne das hier landete der Fokus auf <body>. */
        const active = document.activeElement;
        if (focusOpener && (!active || active === document.body || !document.contains(active))) {
          const opener = this.drawer && this.querySelector(`[data-gcs-open="${this.drawer.id}"]`);
          if (opener) opener.focus({ preventScroll: true });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (GCS.toast) GCS.toast(ERROR, { type: 'error' });
      } finally {
        if (seq === this.seq) this.setLoading(false);
      }
    }

    render(doc) {
      const active = document.activeElement;
      const activeId = active && this.contains(active) ? active.id : '';

      PARTS.forEach((name) => {
        const current = this.querySelector(`[data-gcs-part="${name}"]`);
        const next = doc.querySelector(`[data-gcs-part="${name}"]`);
        if (current && next) current.replaceWith(document.importNode(next, true));
        else if (current && !next && name !== 'toolbar') current.innerHTML = '';
      });
      this.renderFacets(doc);
      this.syncMode();
      this.snapshotFacets();

      if (activeId) {
        const el = document.getElementById(activeId);
        if (el && this.contains(el) && el !== document.activeElement) el.focus({ preventScroll: true });
      }
    }

    renderFacets(doc) {
      const current = this.querySelector('[data-gcs-part="facets"]');
      const next = doc.querySelector('[data-gcs-part="facets"]');
      if (!current || !next) return;
      const open = {};
      current.querySelectorAll('details[data-gcs-facet]').forEach((el) => {
        open[el.dataset.gcsFacet] = el.open;
      });
      const fresh = document.importNode(next, true);
      fresh.querySelectorAll('details[data-gcs-facet]').forEach((el) => {
        if (el.dataset.gcsFacet in open) el.open = open[el.dataset.gcsFacet];
      });
      current.replaceWith(fresh);
    }

    snapshotFacets() {
      const facets = this.querySelector('[data-gcs-part="facets"]');
      this.facetsHtml = facets ? facets.outerHTML : '';
    }

    restoreFacets() {
      const current = this.querySelector('[data-gcs-part="facets"]');
      if (!current || !this.facetsHtml) return;
      const doc = new DOMParser().parseFromString(this.facetsHtml, 'text/html');
      this.renderFacets(doc);
    }

    clearForm() {
      const form = this.form;
      if (!form) return;
      form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
        input.checked = false;
      });
      form.querySelectorAll('[data-gcs-price-range] input').forEach((input) => {
        input.value = '';
      });
      /* Impeccable C5: versteckter Verfügbarkeits-Wert aus einer alten URL („Nicht auf Lager“) */
      form.querySelectorAll('[data-gcs-facet-hidden]').forEach((input) => {
        input.disabled = true;
      });
    }

    /* Sheet: Trefferzahl vorab laden -------------------------------------- */
    async preview() {
      const url = this.urlFromForm();
      this.pendingUrl = url;
      if (this.previewController) this.previewController.abort();
      this.previewController = new AbortController();
      const button = this.querySelector('[data-gcs-facet-apply]');
      if (button) button.setAttribute('aria-busy', 'true');
      try {
        const doc = await this.fetchDoc(url, this.previewController.signal);
        if (url !== this.pendingUrl) return;
        const count = doc.querySelector('[data-gcs-part="count"]');
        this.setApplyLabel(count ? Number(count.dataset.gcsCount) : 0);
        /* Anzahl je Filterwert und nicht mehr wählbare Werte nachziehen, Auswahl bleibt */
        doc.querySelectorAll('[data-gcs-facet-value]').forEach((nextEl) => {
          const el = this.querySelector(`[data-gcs-facet-value="${nextEl.dataset.gcsFacetValue}"]`);
          if (!el) return;
          const input = el.querySelector('input');
          const nextInput = nextEl.querySelector('input');
          if (input && nextInput && !input.checked) input.disabled = nextInput.disabled;
          el.classList.toggle('is-disabled', nextEl.classList.contains('is-disabled') && !(input && input.checked));
          const countEl = el.querySelector('[data-gcs-count-for]');
          const nextCount = nextEl.querySelector('[data-gcs-count-for]');
          if (countEl && nextCount) countEl.textContent = nextCount.textContent;
        });
      } catch (error) {
        if (error.name !== 'AbortError') this.setApplyLabel(null);
      } finally {
        if (url === this.pendingUrl && button) button.removeAttribute('aria-busy');
      }
    }

    setApplyLabel(count) {
      const button = this.querySelector('[data-gcs-facet-apply]');
      if (!button) return;
      const noun = button.dataset.noun || 'Produkte';
      button.disabled = false;
      if (count === null || Number.isNaN(count)) {
        button.textContent = 'Anwenden';
      } else if (count === 0) {
        button.textContent = noun === 'Treffer' ? 'Keine Treffer' : 'Keine Produkte';
        button.disabled = true;
      } else if (count === 1) {
        button.textContent = noun === 'Treffer' ? '1 Treffer anzeigen' : '1 Produkt anzeigen';
      } else {
        button.textContent = `${count} ${noun} anzeigen`;
      }
    }

    /* Mehr laden ---------------------------------------------------------- */
    async loadMore(link) {
      if (link.getAttribute('aria-busy') === 'true') return;
      const url = link.href;
      let fromKeyboard = false;
      try {
        fromKeyboard = document.activeElement === link && link.matches(':focus-visible');
      } catch (e) {
        fromKeyboard = false;
      }
      link.setAttribute('aria-busy', 'true');
      try {
        const doc = await this.fetchDoc(url);
        const results = this.querySelector('[data-gcs-part="results"]');
        const grid = this.querySelector('[data-gcs-grid]');
        const nextGrid = doc.querySelector('[data-gcs-grid]');
        let first = null;
        let added = 0;
        if (nextGrid) {
          const items = Array.from(nextGrid.children).map((item) => document.importNode(item, true));
          items.forEach((item) => item.classList.add('is-new'));
          first = items[0] || null;
          added = items.length;
          if (grid) grid.append(...items);
          else if (results) {
            const fresh = document.importNode(nextGrid, true);
            results.prepend(fresh);
            first = fresh.firstElementChild;
          }
        }
        const pages = this.querySelector('[data-gcs-search-pages] ul');
        const nextPages = doc.querySelector('[data-gcs-search-pages]');
        if (nextPages) {
          if (pages) pages.append(...Array.from(nextPages.querySelectorAll('li')).map((li) => document.importNode(li, true)));
          else if (results) results.append(document.importNode(nextPages, true));
        }
        const pagination = this.querySelector('[data-gcs-part="pagination"]');
        const nextPagination = doc.querySelector('[data-gcs-part="pagination"]');
        if (pagination && nextPagination) pagination.replaceWith(document.importNode(nextPagination, true));

        const clean = new URL(url, window.location.origin);
        clean.searchParams.delete('section_id');
        window.history.replaceState(window.history.state, '', clean.pathname + clean.search);
        const noun = this.classList.contains('gcs-search') ? 'Treffer' : 'Produkte';
        this.say(`${added} weitere ${noun} geladen`);
        if (fromKeyboard && first) {
          const target = first.querySelector('a[href]');
          if (target) target.focus({ preventScroll: false });
        }
      } catch (error) {
        link.removeAttribute('aria-busy');
        if (GCS.toast) GCS.toast(ERROR, { type: 'error' });
      }
    }

    /* Hilfen -------------------------------------------------------------- */
    setLoading(on) {
      this.classList.toggle('is-loading', on);
      const results = this.querySelector('[data-gcs-part="results"]');
      if (results) results.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    say(text) {
      const live = this.querySelector('[data-gcs-live]');
      if (!live) return;
      live.textContent = '';
      window.setTimeout(() => {
        live.textContent = text;
      }, 60);
    }

    announce() {
      const count = this.querySelector('[data-gcs-part="count"]');
      if (count) this.say(count.textContent.trim());
    }

    headerOffset() {
      const value = getComputedStyle(document.documentElement).getPropertyValue('--gcs-header-offset');
      return parseFloat(value) || 0;
    }

    scrollToResults() {
      const main = this.querySelector('.gcs-coll__main');
      if (!main) return;
      const top = main.getBoundingClientRect().top + window.scrollY - this.headerOffset() - 8;
      if (window.scrollY > top + 4) window.scrollTo({ top, behavior: 'smooth' });
    }

    focusResults() {
      const target = this.querySelector('[data-gcs-part="count"]') || this.querySelector('[data-gcs-part="results"]');
      if (!target) return;
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
  }

  customElements.define('gcs-collection', GcsCollection);

  /* <gcs-style-tabs data-section-id>: Style-Tabs über dem Raster. Tab-Klicks sind normale Links (ganze Seite lädt).
     Ändert die Filterleiste den Stand ohne Neuladen, holt sich die Leiste ihren neuen Stand per Section Rendering. */
  class GcsStyleTabs extends HTMLElement {
    connectedCallback() {
      if (this._listening) return;
      this._listening = true;
      this.seq = 0;
      this.onUpdated = (event) => this.refresh(event.detail && event.detail.url);
      document.addEventListener('gcs:collection:updated', this.onUpdated);
    }

    disconnectedCallback() {
      document.removeEventListener('gcs:collection:updated', this.onUpdated);
      this._listening = false;
    }

    async refresh(url) {
      const id = this.dataset.sectionId;
      if (!url || !id) return;
      const target = new URL(url, window.location.origin);
      target.searchParams.delete('page');
      target.searchParams.set('section_id', id);
      const seq = ++this.seq;
      try {
        const response = await fetch(target.pathname + target.search, { headers: { Accept: 'text/html' } });
        if (!response.ok || seq !== this.seq) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = doc.querySelector('gcs-style-tabs');
        if (next && seq === this.seq) this.innerHTML = next.innerHTML;
      } catch (error) {
        /* Leiste bleibt im alten Stand, ihre Links funktionieren weiter */
      }
    }
  }

  if (!customElements.get('gcs-style-tabs')) customElements.define('gcs-style-tabs', GcsStyleTabs);
})();
